import {
  toWSMoneyString,
  WSBadge,
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  InvoicePayoutDestinationType,
  InvoiceStatus,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { MemberWithholdingStatus } from "@wingspanhq/users/dist/lib/interfaces";
import React from "react";
import { useBankingAccount } from "../../../query/bookkeeping/queries";
import { useUserId } from "../../../query/hooks/helpers";
import {
  useInstantPayoutQuery,
  useInvoiceQuery,
  usePayoutSettings
} from "../../../query/payments/queries";
import { getInvoicePayoutDestinationDescription } from "../../../query/payments/selectors";
import { useAccounts, useMemberProfile } from "../../../query/users/queries";
import { WSQueries } from "../../../query/WSQuery";
import { openInNewTab } from "../../../utils/openInNewTab";
import { calculateLineItemsTotal } from "../../utils";
import { getLineItemsTotals } from "../../utils/getLineItemsTotals";
import { getPayoutSettingsCalculations } from "../../utils/payout";
import { Totals, TotalsSection } from "../Totals/Totals";

type Props = {
  invoiceId: string;
} & WSElementProps;

export const InvoiceTotals: React.FC<Props> = ({
  invoiceId,
  ...elementProps
}) => {
  const userId = useUserId();
  const invoiceQuery = useInvoiceQuery(invoiceId);
  const payoutSettingsQuery = usePayoutSettings(userId);
  const accountsQuery = useAccounts();
  const instantPayoutQuery = useInstantPayoutQuery();
  const bankingAccountQuery = useBankingAccount();
  const memberQuery = useMemberProfile(userId);

  return (
    <WSElement {...elementProps}>
      <WSQueries
        queries={{
          invoiceQuery,
          accountsQuery,
          payoutSettingsQuery,
          memberQuery
        }}
      >
        {({
          invoiceQuery: { data: invoice },
          payoutSettingsQuery: { data: payoutSettings },
          accountsQuery: { data: accounts },
          memberQuery: { data: member }
        }) => {
          const {
            isPayoutMethodStandard,
            isPayoutMethodInstant,
            payoutPercentage,
            standardPayoutAccountName,
            instantPayoutAccountName,
            walletAccountName
          } = getPayoutSettingsCalculations({
            payoutSettings,
            accounts,
            instantPayoutAccount: instantPayoutQuery.data,
            bankingAccount: bankingAccountQuery.data
          });

          const totalElement =
            invoice.status !== InvoiceStatus.Paid &&
            invoice.status !== InvoiceStatus.PaymentInTransit ? (
              <WSFlexBox.CenterY>
                <WSText mr="S">Total</WSText>
                <WSBadge leftIcon={{ name: "clock" }} text="Pending" />
              </WSFlexBox.CenterY>
            ) : (
              "Total"
            );

          const totalsSections: TotalsSection[] = [];

          // Line items

          const lateFee = invoice.chargedFees?.lateFee?.amount || 0;
          const lineItemsAmount = calculateLineItemsTotal(invoice.lineItems);
          const lineItemsSum = lineItemsAmount + lateFee;

          const totalsLineItemsSection = getLineItemsTotals(invoice.lineItems);

          if (lateFee > 0) {
            totalsLineItemsSection.items.push([
              "Late fee",
              toWSMoneyString(lateFee)
            ]);
          }

          const deductions = invoice.deductions || [];
          let deductionsRendered = false;

          totalsSections.push(totalsLineItemsSection);

          // Fees & collaborator payments

          const processingFee =
            invoice.creditFeeHandling?.memberPays === 100
              ? (invoice.amountDetails?.processingFee || 0) +
                (invoice.amountDetails?.wingspanTopUp || 0)
              : 0;
          const collaboratorPayments = invoice.amountDetails
            ?.collaboratorPayments
            ? invoice.amountDetails.collaboratorPayments
            : invoice.collaborators
            ? invoice.collaborators.reduce(
                (total, collaborator) => total + collaborator.amount,
                0
              )
            : 0;

          const grossIncome =
            lineItemsSum - processingFee - collaboratorPayments;

          if (processingFee > 0 || collaboratorPayments > 0) {
            const totalsFeesSection: TotalsSection = {
              items: [
                {
                  bold: true,
                  left: totalElement,
                  right: toWSMoneyString(lineItemsSum)
                }
              ]
            };

            if (processingFee) {
              totalsFeesSection.items.push([
                "Processing fee",
                toWSMoneyString(-processingFee)
              ]);
            }

            if (collaboratorPayments) {
              totalsFeesSection.items.push([
                "Payment to collaborators",
                toWSMoneyString(-collaboratorPayments)
              ]);
            }

            totalsSections.push(totalsFeesSection);
          }

          let netIncome = grossIncome;

          // Tax withholdings

          const withholdingTax = member.profile?.withholdings?.tax;
          const withholdingEnabled =
            withholdingTax?.status === MemberWithholdingStatus.Active;
          const invoicePaidOutsidePlarform =
            invoice.status === InvoiceStatus.Paid && !invoice.amountDetails;

          if (withholdingEnabled && !invoicePaidOutsidePlarform) {
            const defaultTaxWthholdingsRate =
              member.profile.withholdings?.tax?.rate || 0;

            const taxWithholding = invoice.amountDetails
              ? invoice.amountDetails.memberTaxWithheld
              : defaultTaxWthholdingsRate
              ? grossIncome * (defaultTaxWthholdingsRate / 100)
              : 0;
            netIncome = grossIncome - taxWithholding;

            if (taxWithholding > 0) {
              deductionsRendered = true;

              const totalsFeesSection: TotalsSection = {
                items: [
                  {
                    bold: true,
                    left:
                      grossIncome === lineItemsSum
                        ? totalElement
                        : "Gross income",
                    right: toWSMoneyString(grossIncome)
                  },
                  ...deductions.map(d => ({
                    left: d.name,
                    right: toWSMoneyString(-d.amount)
                  })),
                  [
                    `Tax withholdings (${
                      invoice.withholdings?.tax
                        ? invoice.withholdings.tax.rate
                        : defaultTaxWthholdingsRate
                    }%)`,
                    toWSMoneyString(-taxWithholding)
                  ]
                ]
              };

              totalsSections.push(totalsFeesSection);
            }
          }

          // Payout destinations

          const instantPayoutFee =
            invoice.processingFees?.instantPayoutFee?.amount || 0;

          const payoutDestinationsSection: TotalsSection = {
            items: [
              {
                bold: true,
                left: netIncome === grossIncome ? totalElement : "Net income",
                right: toWSMoneyString(netIncome)
              },
              ...(deductionsRendered
                ? []
                : deductions.map(d => ({
                    left: d.name,
                    right: toWSMoneyString(-d.amount)
                  })))
            ]
          };

          if (
            invoice.payoutDestinations &&
            invoice.payoutDestinations.length > 0
          ) {
            // Use payoutDestinations which set on invoice
            if (instantPayoutFee > 0) {
              payoutDestinationsSection.items.push([
                "Instant payout fee (1%)",
                toWSMoneyString(-instantPayoutFee)
              ]);
            }

            invoice.payoutDestinations.forEach(payoutDestination => {
              payoutDestinationsSection.items.push(
                payoutDestination.destinationType ===
                  InvoicePayoutDestinationType.WeGift
                  ? [
                      <WSButton.Link
                        rightIcon="arrowRight"
                        onClick={() => {
                          if (payoutDestination.destinationId)
                            openInNewTab(payoutDestination.destinationId);
                        }}
                      >
                        Redeem gift card
                      </WSButton.Link>,
                      null
                    ]
                  : [
                      <WSFlexBox alignItems="center">
                        <WSText color="grey600">
                          {getInvoicePayoutDestinationDescription(
                            payoutDestination
                          )}
                        </WSText>
                        {payoutDestination.payoutMethod ===
                          PayoutPreferences.Instant && (
                          <WSIcon name="wizardStart" ml="S" />
                        )}
                      </WSFlexBox>,
                      toWSMoneyString(payoutDestination.amount)
                    ]
              );
            });
          } else if (invoice.amountDetails) {
            // If there is no payoutDestinations, check acount detials to see if it was paid. We should support it for old invoices, which have no payoutDestinations
            payoutDestinationsSection.items.push({
              left: standardPayoutAccountName
                ? `Direct deposit to ${standardPayoutAccountName}`
                : "Payout",
              right: toWSMoneyString(
                Math.abs(invoice.amountDetails.memberNet || 0)
              )
            });
          } else if (
            payoutSettings.payoutDestinations &&
            payoutSettings.payoutDestinations.length > 0
          ) {
            // In case if invoice has no payoutDestinations and amountDetails set – we gonna show calculated values. We are taking default settings from payout settings

            if (isPayoutMethodInstant) {
              const instantPayoutFee = netIncome * 0.01;
              const instantPayoutAmount = netIncome - instantPayoutFee;
              const accountAmount =
                (instantPayoutAmount * payoutPercentage.instant.account) / 100;
              const walletAmount =
                (instantPayoutAmount * payoutPercentage.instant.wallet) / 100;

              payoutDestinationsSection.items.push([
                "Instant payout fee (1%)",
                toWSMoneyString(-instantPayoutFee)
              ]);

              if (accountAmount > 0) {
                payoutDestinationsSection.items.push([
                  <WSFlexBox.CenterY wrap="nowrap">
                    <WSText color="grey600" mr="S">
                      {instantPayoutAccountName}
                    </WSText>
                    <WSIcon name="wizardStart" size="S" mr="S" />
                  </WSFlexBox.CenterY>,
                  toWSMoneyString(accountAmount)
                ]);
              }

              if (walletAmount > 0) {
                payoutDestinationsSection.items.push([
                  walletAccountName,
                  toWSMoneyString(walletAmount)
                ]);
              }
            } else if (isPayoutMethodStandard) {
              const accountAmount =
                (netIncome * payoutPercentage.standard.account) / 100;
              const walletAmount =
                (netIncome * payoutPercentage.standard.wallet) / 100;

              if (accountAmount > 0) {
                payoutDestinationsSection.items.push([
                  standardPayoutAccountName,
                  toWSMoneyString(accountAmount)
                ]);
              }

              if (walletAmount > 0) {
                payoutDestinationsSection.items.push([
                  walletAccountName,
                  toWSMoneyString(walletAmount)
                ]);
              }
            }
          }

          totalsSections.push(payoutDestinationsSection);

          return <Totals sections={totalsSections} />;
        }}
      </WSQueries>
    </WSElement>
  );
};
